import type {
  HelpArticleDetail,
  HelpArticleSummary,
  HelpScreenArticlesResponse,
  HelpSearchResponse,
  HelpSection,
  HelpSectionArticlesResponse,
  HelpVideosResponse,
} from "@/interfaces/help";
import type { HelpScreenKey } from "@/interfaces/helpScreens";
import { apiClient } from "@/lib/apiClient";
import { buildHelpScreenPath, encodeHelpPathSegment } from "@/utils/help-context-helper";

/**
 * The Support panel's article cut for one or several help screens; several keys are merged by
 * the API into one list (GFG-768).
 */
export async function fetchHelpScreenArticles(
  contextKeys: readonly HelpScreenKey[],
): Promise<HelpArticleSummary[]> {
  const { data } = await apiClient.get<{ success: boolean } & HelpScreenArticlesResponse>(
    buildHelpScreenPath(contextKeys),
  );
  return data.articles;
}

/** One article's full content, as shown when a user opens it from the panel. */
export async function fetchHelpArticle(slug: string): Promise<HelpArticleDetail> {
  const { data } = await apiClient.get<{ success: boolean; article: HelpArticleDetail }>(
    `/help/articles/${encodeHelpPathSegment(slug)}`,
  );
  return data.article;
}

/**
 * Published articles matching a search query, best match first. The query goes as an axios param
 * (URL-encoded as query string), not through encodeHelpPathSegment — that helper is for path
 * segments and rejects "." / "..", which are legitimate search text.
 */
export async function fetchHelpSearchResults(query: string): Promise<HelpArticleSummary[]> {
  const { data } = await apiClient.get<HelpSearchResponse>("/help/search", {
    params: { q: query },
  });
  return data.articles;
}

/** Published articles in one topic, ordered priority asc then title asc. */
export async function fetchHelpSectionArticles(
  section: HelpSection,
): Promise<HelpArticleSummary[]> {
  const { data } = await apiClient.get<HelpSectionArticlesResponse>(
    `/help/sections/${encodeHelpPathSegment(section)}`,
  );
  return data.articles;
}

/** Published articles that carry a walkthrough video, for the panel rail's Watch section. */
export async function fetchHelpVideos(): Promise<HelpArticleSummary[]> {
  const { data } = await apiClient.get<HelpVideosResponse>("/help/videos");
  return data.articles;
}
